import { Button, Dialog, DialogActions, DialogContent, DialogSurface, DialogTitle, Field, Input, Spinner } from "@fluentui/react-components";
import { FC, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { Tendering } from "../../../models/tendering";
import fileService from "../../../services/fileService";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";

interface CreateContractProps {
  open: boolean;
  onClose: () => void;
  tenderingDocument: Tendering;
}

interface FormData {
  [fieldName: string]: string;
}

export const CreateContract: FC<CreateContractProps> = ({ open, onClose, tenderingDocument }) => {
  const fields = tenderingDocument?.properties?.contract?.fields || [];
  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormData>();
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    console.log(tenderingDocument);
    
    // Reset form with initial values when dialog opens
    if (open && tenderingDocument.properties.contract.fields) {
      const initialValues: FormData = {};
      tenderingDocument.properties.contract.fields.forEach(field => {
        initialValues[field.fieldName] = field.value || '';
      });
      reset(initialValues);
    }
  }, [open, tenderingDocument, reset]);

  const onSubmit = async (data: FormData) => {
    setIsGenerating(true);
    try {
      let tags: Record<string, string> = {};
      let file: ArrayBuffer;
      let reportFields = [];

      // Process the form data
      Object.entries(data).forEach(([fieldName, value]) => {
        console.log(`Field: ${fieldName}, Value: ${value}`);
        if (isFieldInTemplate(fieldName)) {
          tags[fieldName] = value;
        }
      });

      const response = await fileService.getFileByID(tenderingDocument.properties.contract.template);
      file = response.data;
      const zip = new PizZip(file);

      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
      });

      doc.render(tags);

      const blob = doc.getZip().generate({
        type: 'blob',
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contract_${tenderingDocument.tenderingID}_${Date.now()}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      onClose();
    } catch (error) {
      console.error('Error generating contract:', error);
      // Handle error as needed - maybe show a toast or error message
    } finally {
      setIsGenerating(false);
    }
  };

  const isFieldInTemplate = (fieldName: string): boolean => {
    return fields.some(field => field.fieldName === fieldName);
  }

  const handleClose = () => {
    if (!isGenerating) {
      reset();
      onClose();
    }
  };

  return (
    <>
      <Dialog 
        open={open} 
        onOpenChange={(event, data) => !data.open && !isGenerating && handleClose()}
      >
        <DialogSurface style={{ maxHeight: '80vh', overflow: 'auto' }}>
          <DialogTitle>
            Skapa avtal
          </DialogTitle>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogContent>
              <div className="mb-4">
                {tenderingDocument.properties.contract.fields.map((field, index) => (
                  <Field key={index} label={field.fieldName} required={field.isRequired}>
                    <Controller
                      name={field.fieldName}
                      control={control}
                      defaultValue={field.value || ''}
                      rules={{ required: field.isRequired ? `${field.fieldName} är obligatoriskt` : false }}
                      render={({ field: controllerField }) => (
                        <Input 
                          {...controllerField}
                          type="text"
                          disabled={isGenerating}
                        />
                      )}
                    />
                    {errors[field.fieldName] && (
                      <div className="text-red-500 text-sm mt-1">
                        {errors[field.fieldName]?.message}
                      </div>
                    )}
                  </Field>
                ))}
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} disabled={isGenerating}>
                Avbryt
              </Button>
              <Button type="submit" appearance="primary" disabled={isGenerating}>
                {isGenerating ? (
                  <>
                    <Spinner size="tiny" className="mr-2" />
                    Genererar avtal...
                  </>
                ) : (
                  'Skapa avtal'
                )}
              </Button>
            </DialogActions>
          </form>
        </DialogSurface>
      </Dialog>
    </>
  );
}

export default CreateContract;